import React, { useState } from "react";
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  Chip,
  Avatar,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Button,
  Stack,
  Alert,
  IconButton,
  Tooltip,
} from "@mui/material";
import {
  Person,
  Business,
  Phone,
  Email,
  AccountBalance,
  People,
  LocalCarWash,
  Warning as WarningIcon,
  SupervisorAccount,
  ContentCopy as ContentCopyIcon,
  Check as CheckIcon,
} from "@mui/icons-material";
import { useTranslation } from "react-i18next";
import { useOwner } from "../../../../context/OwnerContext";
import ownerService from "../../../../services/owner.service";
import AvailabilityChip from "../../../../components/AvailabilityChip";
import AssignRMModal from "../../ManageCarWashes/components/AssignRMModal";
import { useRole } from "../../../../context/RoleContext";

const OwnerInfo = () => {
  const { owner, loading, error, refetch } = useOwner();
  const { t } = useTranslation();
  const { isSuperAdmin } = useRole();
  const [busy, setBusy] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  // Bug 3 (quick task): SuperAdmin previously had no direct way to (re)assign an
  // owner's RM -- AssignRMModal already existed but was only reachable from a
  // pending car-wash review row (ManageCarWashes/components/PendingCarWashReviewRow.tsx),
  // not from the owner's own page. Reused here as-is, no changes to the modal.
  const [assignRMOpen, setAssignRMOpen] = useState(false);
  const [idCopied, setIdCopied] = useState(false);

  // Copy-to-clipboard for the record's _id — replaces the Unique ID table
  // column dropped from ManageOwners. navigator.clipboard.writeText rejects
  // on insecure origins / denied permission, so the rejection is surfaced
  // through the existing actionError alert rather than failing silently.
  const handleCopyId = async () => {
    if (!owner?._id) return;
    try {
      await navigator.clipboard.writeText(owner._id);
      setIdCopied(true);
      setTimeout(() => setIdCopied(false), 2000);
    } catch (err: any) {
      setActionError(t("common.copyIdFailed"));
    }
  };

  // Logged-in admin — used to tell "Added by You" from "Admin Created".
  const currentAdmin = React.useMemo<{ _id?: string; role?: string }>(() => {
    try {
      return JSON.parse(localStorage.getItem("adminData") || "{}");
    } catch {
      return {};
    }
  }, []);

  const runAction = async (
    action: (id: string) => Promise<unknown>,
    failMessage: string
  ) => {
    setBusy(true);
    setActionError(null);
    try {
      await action(owner!._id);
      await refetch();
    } catch (err: any) {
      setActionError(err?.response?.data?.message || err?.message || failMessage);
    } finally {
      setBusy(false);
    }
  };

  if (loading) {
    return (
      <Box className="p-4">
        <Typography>{t("ownerInfo.loading")}</Typography>
      </Box>
    );
  }

  if (error || !owner) {
    return (
      <Box className="p-4">
        <Typography color="error">{error || "Owner not found"}</Typography>
      </Box>
    );
  }

  const getRegistrationStatusColor = (
    status: string | undefined
  ): "warning" | "success" | "error" | "info" | "default" => {
    switch (status) {
      case "pending_approval": return "warning";
      case "approved": return "success";
      case "rejected": return "error";
      case "admin_created": return "info";
      default: return "default";
    }
  };

  // Registration SOURCE (who created the account) — distinct from the approval
  // status. admin_created owners carry the creating admin in relationshipManager;
  // self-registered owners come through the pending_approval flow.
  const getRegistrationSource = (): {
    label: string;
    color: "success" | "info" | "default";
  } => {
    if (owner?.registrationStatus === "admin_created") {
      const creatorId = owner.relationshipManager?._id;
      if (creatorId && currentAdmin?._id && creatorId === currentAdmin._id) {
        return { label: t("ownerInfo.regSourceAddedByYou"), color: "success" };
      }
      return { label: t("ownerInfo.regSourceAdminCreated"), color: "info" };
    }
    return { label: t("ownerInfo.regSourceSelfRegistered"), color: "default" };
  };

  const getBusinessTypeColor = (type: string) => {
    switch (type) {
      case "Citizen": return "primary";
      case "Private": return "success";
      case "Legal": return "warning";
      default: return "default";
    }
  };

  // Derive pending state: owner registration pending OR any car wash has open change requests
  const hasPendingOwnerApproval = owner.registrationStatus === "pending_approval";
  const carWashPendingCount = (owner.carWashes ?? []).reduce(
    (sum, cw) => sum + (cw.pendingReviewCount ?? 0),
    0
  );
  const hasAnyPending = hasPendingOwnerApproval || carWashPendingCount > 0 || owner.hasPendingReview;

  return (
    <Box className="p-4">
      <Box display="flex" alignItems="center" className="mb-6">
        <Typography variant="h4" gutterBottom sx={{ mb: 0 }}>
          {t("ownerInfo.title")}
        </Typography>
        <Tooltip title={idCopied ? t("common.idCopied") : t("common.copyId")}>
          <IconButton size="small" onClick={handleCopyId} sx={{ ml: 1 }}>
            {idCopied ? (
              <CheckIcon fontSize="small" color="success" />
            ) : (
              <ContentCopyIcon fontSize="small" />
            )}
          </IconButton>
        </Tooltip>
      </Box>

      {/* ── Pending indicator — shown above Personal Information when anything needs attention ── */}
      {hasAnyPending && (
        <Alert
          severity="warning"
          icon={<WarningIcon />}
          sx={{ mb: 3, fontWeight: 600 }}
        >
          {hasPendingOwnerApproval && (
            <Typography variant="body2" fontWeight={600}>
              {t("ownerInfo.pendingRegistration")}
            </Typography>
          )}
          {carWashPendingCount > 0 && (
            <Typography variant="body2">
              {t("ownerInfo.pendingCarWashRequests", { count: carWashPendingCount })}
            </Typography>
          )}
          {!hasPendingOwnerApproval && carWashPendingCount === 0 && owner.hasPendingReview && (
            <Typography variant="body2">
              {t("ownerInfo.pendingChangeRequests")}
            </Typography>
          )}
        </Alert>
      )}

      <Grid container spacing={3}>
        <Grid size={{ xs: 12, sm: 6 }} sx={{ display: "flex" }}>
          <Card sx={{ width: "100%", ...(hasAnyPending ? { borderTop: "3px solid", borderColor: "warning.main" } : {}) }}>
            <CardContent>
              <Box className="flex items-center gap-3 mb-4">
                <Avatar sx={{ bgcolor: hasAnyPending ? "warning.main" : "primary.main" }}>
                  {hasAnyPending ? <WarningIcon /> : <Person />}
                </Avatar>
                <Typography variant="h6">{t("ownerInfo.personalInfo")}</Typography>
                {hasAnyPending && (
                  <Chip label={t("ownerInfo.pendingLabel")} color="warning" size="small" sx={{ ml: "auto" }} />
                )}
              </Box>

              <List>
                <ListItem>
                  <ListItemIcon><Person /></ListItemIcon>
                  <ListItemText
                    primary={t("ownerInfo.fullName")}
                    secondary={`${owner?.firstName} ${owner?.lastName}`}
                  />
                </ListItem>

                <ListItem>
                  <ListItemIcon><Phone /></ListItemIcon>
                  <ListItemText
                    primary={t("ownerInfo.phone")}
                    secondary={`${owner?.country_code} ${owner?.phone}`}
                  />
                </ListItem>

                {owner?.email && (
                  <ListItem>
                    <ListItemIcon><Email /></ListItemIcon>
                    <ListItemText primary={t("ownerInfo.email")} secondary={owner?.email} />
                  </ListItem>
                )}

                {owner?.gender && (
                  <ListItem>
                    <ListItemIcon><Person /></ListItemIcon>
                    <ListItemText primary={t("ownerInfo.gender")} secondary={owner?.gender} />
                  </ListItem>
                )}

                {owner?.dateOfBirth && (
                  <ListItem>
                    <ListItemIcon><Person /></ListItemIcon>
                    <ListItemText
                      primary={t("ownerInfo.dateOfBirth")}
                      secondary={new Date(owner?.dateOfBirth).toLocaleDateString()}
                    />
                  </ListItem>
                )}

                {isSuperAdmin && (
                  <ListItem
                    secondaryAction={
                      <Button
                        size="small"
                        variant="outlined"
                        onClick={() => setAssignRMOpen(true)}
                      >
                        {owner?.relationshipManager
                          ? t("manageCarWashes.changeRM")
                          : t("manageCarWashes.assignRMTitle")}
                      </Button>
                    }
                  >
                    <ListItemIcon><SupervisorAccount /></ListItemIcon>
                    <ListItemText
                      primary={t("rm.title")}
                      secondary={
                        owner?.relationshipManager
                          ? `${owner.relationshipManager.firstName ?? ""} ${owner.relationshipManager.lastName ?? ""}`.trim() ||
                            owner.relationshipManager.username
                          : t("rm.noRM")
                      }
                    />
                  </ListItem>
                )}
              </List>
            </CardContent>
          </Card>
        </Grid>

        <Grid size={{ xs: 12, sm: 6 }} sx={{ display: "flex" }}>
          <Card sx={{ width: "100%" }}>
            <CardContent>
              <Box className="flex items-center gap-3 mb-4">
                <Avatar sx={{ bgcolor: "success.main" }}>
                  <Business />
                </Avatar>
                <Typography variant="h6">{t("ownerInfo.businessInfo")}</Typography>
              </Box>

              <List>
                <ListItem>
                  <ListItemIcon><Business /></ListItemIcon>
                  <ListItemText
                    primary={t("ownerInfo.businessType")}
                    secondary={
                      owner?.businessType ? (
                        <Chip
                          label={owner?.businessType}
                          color={getBusinessTypeColor(owner?.businessType) as any}
                          size="small"
                        />
                      ) : (
                        t("ownerInfo.notSpecified")
                      )
                    }
                  />
                </ListItem>

                {owner?.voen && (
                  <ListItem>
                    <ListItemIcon><Business /></ListItemIcon>
                    <ListItemText primary={t("ownerInfo.voen")} secondary={owner?.voen} />
                  </ListItem>
                )}

                <ListItem>
                  <ListItemIcon><AccountBalance /></ListItemIcon>
                  <ListItemText
                    primary={t("ownerInfo.balance")}
                    secondary={`₼ ${owner?.balance || 0}`}
                  />
                </ListItem>

                <ListItem>
                  <ListItemIcon><LocalCarWash /></ListItemIcon>
                  <ListItemText
                    primary={t("ownerInfo.carWashesCount", { count: owner?.carWashes?.length || 0 })}
                  />
                </ListItem>

                <ListItem>
                  <ListItemIcon><People /></ListItemIcon>
                  <ListItemText
                    primary={t("ownerInfo.employeesCount", { count: owner?.employees?.length || 0 })}
                  />
                </ListItem>
              </List>
            </CardContent>
          </Card>
        </Grid>

        {owner?.registrationStatus === "pending_approval" && (
          <Grid size={{ xs: 12 }}>
            <Card>
              <CardContent>
                <Box className="flex items-center gap-3 mb-4">
                  <Avatar sx={{ bgcolor: "warning.main" }}>
                    <Person />
                  </Avatar>
                  <Typography variant="h6">{t("ownerInfo.registrationActions")}</Typography>
                </Box>

                {actionError && (
                  <Alert severity="error" sx={{ mb: 2 }}>
                    {actionError}
                  </Alert>
                )}

                {/* An approved Owner must always have an RM: once registrationStatus
                    flips to "approved", runPendingRotationSweep no longer matches them
                    (it queries pending_approval only), so nothing will ever assign one
                    and the owner becomes invisible to the whole RM layer. The backend
                    refuses such an approval with a 400; surfacing it here means the
                    operator is told BEFORE clicking, next to the Assign RM control that
                    fixes it. */}
                {!owner.relationshipManager && (
                  <Alert severity="warning" sx={{ mb: 2 }}>
                    {t("ownerInfo.rmRequiredToApprove")}
                  </Alert>
                )}

                <Stack direction="row" spacing={1}>
                  {/* "Start review" is the ASSIGNED RM claiming their 24h SLA window —
                      it stamps pendingStartedAt, which the SLA sweep measures reminders
                      and escalations against, and POST /owners/:id/start is restricted
                      to relationship_manager for exactly that reason. A super_admin
                      pressing it would record that the RM began work they never began
                      and suppress the escalation that should fire against them.

                      Because this is an either/or, leaving Start visible to a
                      super_admin also HID Accept/Reject behind a button that 403s, so
                      they could not reach the decision at all. Accept and reject require
                      no prior start, so a super_admin goes straight to them.
                      Same fix as PendingTableRow.tsx — these are the only two sites. */}
                  {!owner.pendingStartedAt && !isSuperAdmin ? (
                    <Button
                      variant="contained"
                      disabled={busy}
                      onClick={() =>
                        runAction(
                          (id) => ownerService.startOwner(id),
                          "Failed to start review"
                        )
                      }
                    >
                      {t("ownerInfo.startReview")}
                    </Button>
                  ) : (
                    <>
                      <Button
                        variant="contained"
                        color="success"
                        disabled={busy || !owner.relationshipManager}
                        onClick={() =>
                          runAction(
                            (id) => ownerService.acceptOwner(id),
                            "Failed to accept owner"
                          )
                        }
                      >
                        {t("ownerInfo.accept")}
                      </Button>
                      <Button
                        variant="outlined"
                        color="error"
                        disabled={busy}
                        onClick={() =>
                          runAction(
                            (id) => ownerService.rejectOwner(id),
                            "Failed to reject owner"
                          )
                        }
                      >
                        {t("ownerInfo.reject")}
                      </Button>
                    </>
                  )}
                </Stack>
              </CardContent>
            </Card>
          </Grid>
        )}

        <Grid size={{ xs: 12 }} sx={{ display: "flex" }}>
          <Card sx={{ width: "100%" }}>
            <CardContent>
              <Box className="flex items-center gap-3 mb-4">
                <Avatar
                  sx={{
                    bgcolor: `${getRegistrationStatusColor(owner?.registrationStatus)}.main`,
                  }}
                >
                  <Person />
                </Avatar>
                <Typography variant="h6">{t("ownerInfo.accountStatus")}</Typography>
              </Box>

              <List>
                {(() => {
                  const source = getRegistrationSource();
                  return (
                    <ListItem>
                      <ListItemIcon><Person /></ListItemIcon>
                      <ListItemText
                        primary={t("ownerInfo.registrationStatus")}
                        secondary={
                          <Chip
                            label={source.label}
                            color={source.color}
                            size="small"
                          />
                        }
                      />
                    </ListItem>
                  );
                })()}

                <ListItem>
                  <ListItemIcon><Person /></ListItemIcon>
                  <ListItemText
                    primary={t("ownerInfo.availability")}
                    secondary={
                      <AvailabilityChip status={owner?.availabilityStatus} />
                    }
                  />
                </ListItem>

                <ListItem>
                  <ListItemIcon><Person /></ListItemIcon>
                  <ListItemText
                    primary={t("ownerInfo.created")}
                    secondary={new Date(owner?.createdAt).toLocaleDateString()}
                  />
                </ListItem>

                <ListItem>
                  <ListItemIcon><Person /></ListItemIcon>
                  <ListItemText
                    primary={t("ownerInfo.lastUpdated")}
                    secondary={new Date(owner?.updatedAt).toLocaleDateString()}
                  />
                </ListItem>
              </List>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {isSuperAdmin && owner && (
        <AssignRMModal
          open={assignRMOpen}
          onClose={() => setAssignRMOpen(false)}
          ownerId={owner._id}
          onAssigned={refetch}
          onError={setActionError}
          currentRM={owner.relationshipManager}
        />
      )}
    </Box>
  );
};

export default OwnerInfo;
